import i18n from '@/i18n';
import { cropImageToContent } from '@/utils/imageUpload';
import { getImageFromIndexedDB } from '@/utils/imageStorage';
import { ensureDisplayImageUrls, toAbsoluteViewPath } from '@/utils/imageDisplayAccess';
import { resolveFetchableImageUrl } from '@/utils/imageUrlUtils';
import { normalizeGoodsImageRefToId } from './goodsTemplateImageUrls';
import {
  drawStampImageInArea,
  mapStampAreaToTemplatePixels,
  resolveWorkspaceSizeForSlide,
} from './stampCanvasComposite';

function isDirectCanvasImageSrc(url) {
  const s = String(url || '').trim();
  return !s || s.startsWith('data:') || s.startsWith('blob:');
}

/** Canvas 合成 Image.src：受管 /uploads/ 与 OSS https 先 sign-batch，signed-only 失败返回 '' */
export async function resolveCanvasCompositeImageSrc(rawUrl, itemOrOptions) {
  const trimmed = String(rawUrl || '').trim();
  if (isDirectCanvasImageSrc(trimmed)) return trimmed;
  const signed = await resolveFetchableImageUrl(trimmed, itemOrOptions);
  if (!signed) return '';
  if (
    signed.startsWith('http://')
    || signed.startsWith('https://')
    || signed.startsWith('data:')
    || signed.startsWith('blob:')
  ) {
    return signed;
  }
  return toAbsoluteViewPath(signed);
}

export async function prefetchCanvasCompositeImageSrcs(urls) {
  const list = [
    ...new Set(
      (Array.isArray(urls) ? urls : [])
        .map((u) => String(u || '').trim())
        .filter((u) => u && !isDirectCanvasImageSrc(u)),
    ),
  ];
  if (list.length > 0) {
    await ensureDisplayImageUrls(list);
  }
}

function collectStampCompositeRawUrls(stampAreas, imageGroup, imageUrlMap) {
  const rawUrls = [];
  for (const slideId of Object.keys(stampAreas || {})) {
    const stamps = stampAreas[slideId];
    if (!Array.isArray(stamps)) continue;
    for (const stamp of stamps) {
      const imageId = normalizeGoodsImageRefToId(imageGroup[stamp.name]);
      if (!imageId) continue;
      const url = imageUrlMap[imageId];
      if (url) rawUrls.push(url);
    }
  }
  return rawUrls;
}

/**
 * 浏览器端将印花贴入套图模版（与 IPE 设计器参考印花一致，供工作流批量调用）。
 * @param {string} templateImageUrl 底图 URL / data URL
 * @param {Record<string, Array>} stampAreas { [templateSlideId]: StampArea[] }
 * @param {Record<string, string>} imageGroup 印花名 -> 图片空间 id（或 id 字符串）
 * @param {Record<string, string>} imageUrlMap 图片 id -> 可绘制 URL / data URL（会被原地补全）
 * @param {{ templateData?: object, templateImageId?: string }} [options]
 */
export async function compositeStampOntoTemplateDataUrl(
  templateImageUrl,
  stampAreas,
  imageGroup,
  imageUrlMap,
  options = {},
) {
  await prefetchCanvasCompositeImageSrcs([
    templateImageUrl,
    ...collectStampCompositeRawUrls(stampAreas, imageGroup, imageUrlMap),
  ]);
  const templateSrc = await resolveCanvasCompositeImageSrc(templateImageUrl);
  if (!templateSrc) {
    throw new Error(i18n.t('workbench:ipeUtilError.templateImageUrlUnresolved'));
  }

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const templateImg = new Image();
    templateImg.crossOrigin = 'anonymous';
    templateImg.onload = async () => {
      canvas.width = templateImg.naturalWidth;
      canvas.height = templateImg.naturalHeight;

      ctx.drawImage(templateImg, 0, 0, templateImg.naturalWidth, templateImg.naturalHeight);

      const slideIds = Object.keys(stampAreas || {});

      for (const slideId of slideIds) {
        const stamps = stampAreas[slideId];
        if (!Array.isArray(stamps)) continue;

        const workspaceSize = resolveWorkspaceSizeForSlide(
          options.templateImageId || slideId,
          options.templateData,
          templateImg.naturalWidth,
          templateImg.naturalHeight,
        );

        for (const stamp of stamps) {
          const imageRef = imageGroup[stamp.name];
          const imageId = normalizeGoodsImageRefToId(imageRef);
          if (!imageId) continue;

          let imageUrl = imageUrlMap[imageId];
          if (!imageUrl) {
            try {
              imageUrl = await getImageFromIndexedDB(imageId);
              if (imageUrl) imageUrlMap[imageId] = imageUrl;
            } catch {
              /* ignore */
            }
          }
          if (!imageUrl) continue;

          const drawableUrl = await resolveCanvasCompositeImageSrc(imageUrl);
          if (!drawableUrl) continue;

          let croppedImageUrl = drawableUrl;
          try {
            croppedImageUrl = await cropImageToContent(drawableUrl);
          } catch {
            croppedImageUrl = drawableUrl;
          }

          const mapped = mapStampAreaToTemplatePixels(
            stamp,
            templateImg.naturalWidth,
            templateImg.naturalHeight,
            workspaceSize,
          );

          const stampImg = new Image();
          stampImg.crossOrigin = 'anonymous';
          await new Promise((imgResolve, imgReject) => {
            stampImg.onload = () => {
              drawStampImageInArea(ctx, stampImg, stamp, mapped);
              imgResolve();
            };
            stampImg.onerror = imgReject;
            stampImg.src = croppedImageUrl;
          });
        }
      }

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => {
              resolve(URL.createObjectURL(blob));
            };
            reader.readAsDataURL(blob);
          } else {
            reject(new Error('无法生成合成图片'));
          }
        },
        'image/jpeg',
        0.95,
      );
    };

    templateImg.onerror = () => reject(new Error('无法加载模版图片'));
    templateImg.src = templateSrc;
  });
}
